import { Edit, X, Coffee } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState, useEffect } from "react";
import type { Server } from "../../types";
import { apiFetch } from "../../utils/api";
import { selectableLaunchFiles } from "../../utils/launchFiles";
import { Select } from "../design/Select";

interface JavaVersion {
  path: string;
  name: string;
  version: string;
}

const EditServerModal = ({ server, onClose, onSave }: { server: Server, onClose: () => void, onSave: (data: any) => Promise<unknown> }) => {
  const [name, setName] = useState(server.name);
  const [ram, setRam] = useState(server.ram_limit);
  const [port, setPort] = useState(server.port);
  const [javaPath, setJavaPath] = useState(server.java_path || 'java');
  const [javaVersions, setJavaVersions] = useState<JavaVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingJava, setLoadingJava] = useState(true);
  const [customDomain, setCustomDomain] = useState(server.custom_domain || '');
  const [launchMode, setLaunchMode] = useState<'default' | 'custom_jar'>(server.launch_config?.mode || 'default');
  const [jarFile, setJarFile] = useState(server.launch_config?.jar_file || '');
  const [availableJars, setAvailableJars] = useState<string[]>([]);

  useEffect(() => {
    if (launchMode === 'custom_jar') {
       // Fetch jars
       apiFetch(`/api/servers/${server.id}/files`)
         .then(res => res.json())
         .then(data => {
            if (data.success && data.files) {
                // Modloader-Installer fliegen raus — sie starten keinen Server.
                const jars = selectableLaunchFiles(
                    data.files
                      .filter((f: any) => !f.isDirectory && f.name.endsWith('.jar'))
                      .map((f: any) => f.name));
                setAvailableJars(jars);
            }
         })
         .catch(e => console.error("Failed to fetch jars", e));
    }
  }, [launchMode, server.id]);

  useEffect(() => {
    const fetchJavaVersions = async () => {
      try {
        const res = await apiFetch('/api/servers/java-versions');
        const data = await res.json();
        if (data.success) {
          setJavaVersions(data.versions);
          if (!server.java_path && data.versions.length > 0) {
            setJavaPath(data.versions[0].path);
          }
        }
      } catch (e) {
        console.error('Failed to fetch Java versions', e);
      } finally {
        setLoadingJava(false);
      }
    };
    fetchJavaVersions();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await onSave({ 
        id: server.id, 
        name, 
        ram_limit: ram, 
        port, 
        java_path: javaPath,
        custom_domain: customDomain,
        launch_config: {
            mode: launchMode,
            jar_file: launchMode === 'custom_jar' ? jarFile : undefined
        }
    });
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-4">
      <div className="bg-surface-2 rounded-2xl border border-line p-6 w-full max-w-md shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white flex items-center"><Edit size={24} className="mr-2" /> Server Bearbeiten</h2>
          <button onClick={onClose} className="p-2 text-fg-muted hover:text-white hover:bg-surface-2 rounded-lg transition-all transform hover:scale-110 active:scale-95"><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-fg-muted text-sm font-bold mb-2">Server Name</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full bg-ink border border-line text-white rounded p-3 focus:border-blue-500 outline-none" />
          </div>
          <div>
            <label className="block text-fg-muted text-sm font-bold mb-2">Custom Domain</label>
            <input type="text" value={customDomain} onChange={e => setCustomDomain(e.target.value)} placeholder="srv.myserver.com" className="w-full bg-ink border border-line text-white rounded p-3 focus:border-blue-500 outline-none" />
          </div>
          <div>
            <label className="block text-fg-muted text-sm font-bold mb-2">Start Method</label>
            <div className="space-y-2">
                <Select
                    value={launchMode}
                    onChange={(v) => setLaunchMode(v as any)}
                    options={[
                        { value: 'default', label: 'Standard (server.jar / run.sh)' },
                        { value: 'custom_jar', label: 'Custom JAR Datei' },
                    ]}
                />
                {launchMode === 'custom_jar' && (
                    <Select
                        value={jarFile}
                        onChange={setJarFile}
                        placeholder="Wähle .jar Datei…"
                        options={availableJars.map(jar => ({ value: jar, label: jar }))}
                    />
                )}
            </div>
          </div>
          <div>
            <label className="block text-fg-muted text-sm font-bold mb-2">Port</label>
            <input type="number" value={port} onChange={e => setPort(parseInt(e.target.value))} className="w-full bg-ink border border-line text-white rounded p-3 focus:border-blue-500 outline-none" />
          </div>
          <div>
            <label className="block text-fg-muted text-sm font-bold mb-2">RAM Limit (GB)</label>
            <div className="flex items-center space-x-4">
              <input type="range" min="1" max="32" value={ram} onChange={e => setRam(parseInt(e.target.value))} className="flex-1" />
              <span className="text-white font-mono w-12 text-right">{ram} GB</span>
            </div>
          </div>
          <div>
            <label className="block text-fg-muted text-sm font-bold mb-2 flex items-center">
              <Coffee size={14} className="mr-2 text-orange-400" /> Java Version
            </label>
            {loadingJava ? (
              <div className="w-full bg-ink border border-line text-fg-muted rounded p-3">Lade Java Versionen...</div>
            ) : (
              <Select
                value={javaPath}
                onChange={setJavaPath}
                options={javaVersions.map((v) => ({ value: v.path, label: v.name, hint: v.version }))}
              />
            )}
          </div>
          <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-lg flex items-center justify-center mt-4 shadow-lg hover:shadow-blue-500/25">
            {loading ? 'Speichere...' : 'Änderungen Speichern'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default observer(EditServerModal);